import { FC, Ref, useState } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { useIsProductPageMutating } from '@telialt/core-eshop/src/hooks/useIsProductPageMutating';
import { HybrisOrderCart, HybrisOrderCartProps } from '@telialt/core-eshop/src/HybrisOrderCart/HybrisOrderCart';
import { trackAddToCartGA } from '@telialt/gtm-tracking/src/events';
import { Cart } from '@telialt/models/src/EshopModel';
import { selectPossibleSapRatePlans } from '@telialt/utils/src/eshopSelectors';
import { isEmpty } from '@telialt/utils/src/helpers';
import { TeliaSegment, useSegment } from '@telialt/utils/src/segment';
import { useUserData } from '@telialt/utils/src/SSO/useUserData';

type Props = {
    cartTotalsRef?: Ref<HTMLElement>;
};

const getNextStepUrl = (segment: TeliaSegment, isUserAuthenticated: boolean, cart: Cart) => {
    const cartGuid = cart.guid;

    if (segment === TeliaSegment.B2b) {
        return `/verslui/prekes/uzsakymas-priedai?cartGuid=${cartGuid}`;
    }
    if (!isUserAuthenticated) {
        return `/privatiems/pirkimas/prisijungimas?cartGuid=${cartGuid}`;
    }
    const possibleSapRatePlans = selectPossibleSapRatePlans(cart);
    if (isUserAuthenticated && !!possibleSapRatePlans?.length) {
        return '/privatiems/pirkimas/planas';
    }

    return `/privatiems/prekes/uzsakymas-priedai?cartGuid=${cartGuid}`;
};

export const ProductOrderCart: FC<Props> = ({ cartTotalsRef }) => {
    const { t } = useTranslations();
    const segment = useSegment();
    const { cart } = useCartQuery();
    const { userData } = useUserData();

    const [isLoading, setIsLoading] = useState(false);

    const isProductPageMutating = useIsProductPageMutating();

    const possibleSapRatePlans = selectPossibleSapRatePlans(cart);
    const showRatePlanRelatedEntries = !possibleSapRatePlans?.length || segment === TeliaSegment.B2b;

    const actionText =
        !!possibleSapRatePlans?.length && segment === TeliaSegment.B2c
            ? t['eshop.order_cart.primary_action.text_rate_plan']
            : t['eshop.order_cart.primary_action.text'];

    const isActionDisabled = isProductPageMutating;

    const nextStepUrl = getNextStepUrl(segment, !!userData, cart);

    const handleGoToNextStep = () => {
        if (isActionDisabled || isLoading) {
            return;
        }

        setIsLoading(true);
        if (!isEmpty(cart?.entries)) {
            trackAddToCartGA({ source: 'productPage', data: { cart } });
        }

        window.location.assign(nextStepUrl);
    };

    const header: HybrisOrderCartProps['header'] = {
        expandable: 'ALWAYS',
        isDefaultExpanded: true,
    };
    const totals: HybrisOrderCartProps['totals'] = {
        isTotalPricesWithoutRatePlan: !showRatePlanRelatedEntries,
        cartTotalsRef,
    };
    const action: HybrisOrderCartProps['action'] = {
        onClick: handleGoToNextStep,
        hasArrow: true,
        isDisabled: isActionDisabled,
        isLoading,
        text: actionText,
    };

    return (
        <HybrisOrderCart
            header={header}
            totals={totals}
            action={action}
            showValuePrompts={true}
            showRatePlanRelatedEntries={showRatePlanRelatedEntries}
        />
    );
};
